import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { SelectChangeEvent } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import { CustomAlert, CustomButton, CustomSelect, Picker, SearchSelect } from '../../../shared/uiComponents';
import { Client, SessionType } from '../../../shared/Assets/sessionDetailsSVGs';
import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { SelectDataProperties } from '../../../shared/uiComponents/Dropdown';
import { SelectDataProperties as SearchDataProperties } from '../../../shared/uiComponents/Dropdown/searchSelect';
import { TimeInputsWrapper } from '../../Reports/helpers';
import { AdminTypes } from '../../../components/Menu';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { SessionDataProperties } from '../../../redux/API/ClientAPIHelpers/sessionsProperties';
import { getUserClientsStartSession } from '../../../redux/State/clientSlice/userClientSlice';
import { getSessionTypes, startSession } from '../../../redux/State/clientSlice/sessionSlice';
import {
	setAddInstantSession,
	setClient,
	setSearchClient,
	setTherapist,
} from '../../../redux/State/clientSlice/calendarHelperSlice';
import {
	clearDiscretionarySessionState,
	getUserRemainingHours,
} from '../../../redux/State/userSlice/userSessionAllocationSlice';

const todayDayjs = dayjs();
const meetAndGreetSessionTypeIds = [7, 8];

const Content = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();

	const userId = useSelector((state) => state.account.user.id);
	const sessionTypesData = useSelector((state) => state.session.sessionTypes);
	const role = useSelector((state) => state.account.role);
	const clientId = useSelector((state) => state.calendarHelper.filterData.client?.id);
	const searchString = useSelector((state) => state.calendarHelper.filterData.searchClient);
	const userSessionAllocation = useSelector((state) => state.userSessionAllocation.userRemainingHours);

	const [clientList, setClientList] = useState<SearchDataProperties[]>([]);
	const [hasMoreClients, setHasMoreClients] = useState<boolean>(false);
	const [endTimeValue, setEndTimeValue] = useState<Dayjs>(todayDayjs.add(1, 'hour'));
	const [pageSize, setPageSize] = useState<number>(10);
	const [sessionTypeId, setSessionTypeId] = useState<number>(0);
	const [sessionTypes, setSessionTypes] = useState<SelectDataProperties[]>([]);

	const isBT = role?.section.id === AdminTypes.bt;
	const canSkipSession = (Boolean(sessionTypeId) && meetAndGreetSessionTypeIds.includes(sessionTypeId)) || !isBT;

	const onClientChange = (value: string) => {
		const client = clientList.find((x) => x.id === value);
		if (!client) return;
		dispatch(setClient(client));
	};

	const onTypeChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		setSessionTypeId(parseInt(value));
	};

	const onEndDateChange = (date: Dayjs | null) => {
		if (!date) return;
		setEndTimeValue(date);
	};

	const startSessionHandler = () => {
		if (!clientId || !sessionTypeId) return;

		const date = dateNormalizer(todayDayjs);

		const data: SessionDataProperties = {
			clientId,
			date,
			endTime: endTimeValue?.format('YYYY-MM-DDTHH:mm'),
			sessionTypeId,
			startTime: todayDayjs.format('YYYY-MM-DDTHH:mm'),
		};
		dispatch(startSession(data))
			.then(unwrapResult)
			.then((response) => {
				if (!response.id || !response.client.id) return;
				dispatch(setClient(null));
				dispatch(setTherapist(null));
				onClose();

				if (!canSkipSession) {
					navigate(`/calendar/session/${response.id}/${response.client.id}`, {
						state: response,
					});
				}
			})
			.catch(errorNormalizer);
	};

	useEffect(() => {
		if (!userId) return;
		const timeout = setTimeout(() => {
			dispatch(
				getUserClientsStartSession({
					userId,
					page: '1',
					pageSize: pageSize.toString(),
					searchString,
				}),
			)
				.then(unwrapResult)
				.then((response) => {
					const { query, hasNextPage } = response;
					if (!query) return;
					const clientListValues = query.map((client) => ({
						id: client.id as string,
						label: client.fullName,
					}));

					if (!clientListValues.length) {
						setClient(null);

						return;
					}
					setClientList(clientListValues);
					setHasMoreClients(Boolean(hasNextPage));
				})
				.catch(errorNormalizer);
		}, 1000);

		// TODO - clearTimeout to void
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [userId, pageSize, searchString, dispatch]);

	useEffect(() => {
		const data = sessionTypesData.filter((sessionType) =>
			sessionType.allowedRoleSections?.find((x) => x.id === role?.section.id),
		);
		setSessionTypes(data);
		if (!isBT && data.length !== 1) return;
		setSessionTypeId(3);
	}, [sessionTypesData, role, isBT]);

	useEffect(() => {
		if (!clientId) return;
		dispatch(getSessionTypes({ clientId, date: dateNormalizer(todayDayjs), userId }));
	}, [dispatch, clientId, userId]);

	const searchClientHandler = (value: string) => {
		dispatch(setSearchClient(value));
	};

	useEffect(() => {
		if (!sessionTypeId || sessionTypeId !== 9) return;
		const date = dateNormalizer(todayDayjs);
		dispatch(getUserRemainingHours({ userId, date, sessionType: sessionTypeId }));
	}, [dispatch, userId, sessionTypeId]);

	return (
		<>
			<SearchSelect
				data={clientList}
				label="Client"
				setSearchString={searchClientHandler}
				searchString={searchString}
				setValue={onClientChange}
				hasMore={hasMoreClients}
				setSize={setPageSize}
				startAdornment={<Client />}
				placeholder="Select client"
			/>
			{Boolean(sessionTypes) && (
				<CustomSelect
					label="Session type"
					data={sessionTypes}
					value={`${sessionTypeId}`}
					setValue={onTypeChange}
					className="marginBottom16"
					disabled={!clientId}
					startAdornment={<SessionType />}
					description={
						userSessionAllocation
							? `${userSessionAllocation.remainingHoursThisMonth} hours remaining out of ${userSessionAllocation.allocatedHoursPerMonth}.`
							: ''
					}
				/>
			)}
			<Picker.CustomDate
				label="Date of session"
				onChange={() => null}
				value={todayDayjs}
				className="marginBottom16"
				disabled
			/>
			<TimeInputsWrapper>
				<Picker.CustomTime label="Start time" value={todayDayjs} onChange={() => null} disabled />
				<Picker.CustomTime label="End time" value={endTimeValue} onChange={onEndDateChange} />
			</TimeInputsWrapper>

			<CustomButton
				onClick={startSessionHandler}
				title="Start Session"
				disabled={!endTimeValue || !clientId || clientId === '0' || sessionTypeId === 0}
			/>
		</>
	);
};

const AddInstantSession = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const open = useSelector((state) => state.calendarHelper.popups.addInstant);
	const isAddingInstantSession = useSelector((state) => state.session.loading);

	const closeHandler = () => {
		dispatch(setAddInstantSession(false));
		dispatch(clearDiscretionarySessionState());
	};

	return (
		<CustomAlert
			isBusy={isAddingInstantSession}
			open={open}
			onClose={closeHandler}
			title="Add session"
			Content={Content}
			titleOnTop={false}
		/>
	);
};

export default AddInstantSession;
